// PSTUDIO-3865: pure, React/design-system-free layout for the node-group header
// row. The four header elements — the top node's number, the node-count badge,
// the group-name input, and the action buttons — are bound POSITIONALLY to the
// first four VISIBLE non-control columns (in render order, pinning included),
// NOT to specific column ids. This replaces the earlier absolutely-positioned
// "strip" + offset math (group-strip-layout.ts), whose anchor/offset drifted
// and let the fields overlap once a column was pinned/reordered. Each element
// now lives in its own <td> and is clipped to that column, so nothing can
// collide regardless of which columns are pinned, reordered, or hidden.

/** The four content elements of the group header, in fixed left-to-right order. */
export type GroupHeaderSlot = 'nodeNumber' | 'count' | 'name' | 'actions';

/** Minimal view of a table column the layout needs. */
export interface GroupHeaderColumn {
  id: string;
  /**
   * True ONLY for the control column (leading index/chevron+drag cell, cell
   * kind `'control'`). It stays a dedicated service column and NEVER hosts a
   * content slot; the four content slots fill the columns AFTER it.
   */
  isControl: boolean;
  /**
   * The custom `columnDef.hidden` flag. A hidden column still exists in the
   * row's `getVisibleCells()` (it is only `display:none`), so it must NOT
   * consume a positional slot.
   */
  hidden: boolean;
}

export interface GroupHeaderLayout {
  /**
   * For each input column index, the header slots it must render — in order.
   * Usually 0 or 1 slot per column; when there are fewer than four visible
   * content columns the trailing slots clamp onto the last one (so the action
   * buttons are never dropped), giving that cell 2+ slots.
   */
  slotsByColIndex: Record<number, GroupHeaderSlot[]>;
}

/** Fixed slot order: number → count → name → actions (left-to-right). */
const CONTENT_SLOTS: GroupHeaderSlot[] = [
  'nodeNumber',
  'count',
  'name',
  'actions',
];

/**
 * Assign the four content slots to the first VISIBLE non-control columns by
 * POSITION (pin/reorder-independent). If fewer than four such columns exist,
 * trailing slots clamp to the last visible content column so nothing is lost.
 */
export const computeGroupHeaderLayout = (
  cols: GroupHeaderColumn[]
): GroupHeaderLayout => {
  const slotsByColIndex: Record<number, GroupHeaderSlot[]> = {};

  // Indices of the VISIBLE, non-control columns in render order — these host
  // the content slots. Pinned columns are already first in `cols` (TanStack
  // getVisibleCells order), so position here === on-screen position.
  const contentColIndices: number[] = [];
  cols.forEach((c, i) => {
    if (!c.isControl && !c.hidden) contentColIndices.push(i);
  });

  if (contentColIndices.length === 0) return { slotsByColIndex };

  CONTENT_SLOTS.forEach((slot, slotIdx) => {
    // Clamp to the last visible content column so a short column set still
    // renders every slot (esp. the action buttons) rather than dropping it.
    const pos = Math.min(slotIdx, contentColIndices.length - 1);
    const colIdx = contentColIndices[pos];
    (slotsByColIndex[colIdx] ??= []).push(slot);
  });

  return { slotsByColIndex };
};
